import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { 
  createRememberMeSession, 
  removeRememberMeSession,
  logoutAllDevices as logoutAllDevicesLib
} from '../lib/rememberMe';
import { useSessionRefresh } from '@/hooks/useSessionRefresh';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, metadata?: Record<string, any>) => Promise<{ error: any }>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: any; data?: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  logoutAllDevices: () => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Verificação de segurança para React
  if (typeof React === 'undefined' || !React.useState) {
    console.error('React não está disponível no AuthProvider');
    return <>{children}</>;
  }

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Hook para gerenciar refresh de sessão quando a aba volta a ficar ativa
  useSessionRefresh();

  useEffect(() => {
    console.log('[AuthContext] Initializing auth...');
    console.log('[AuthContext] Hostname:', window.location.hostname);
    console.log('[AuthContext] Protocol:', window.location.protocol);
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[AuthContext] Auth state changed:', event);
        console.log('[AuthContext] Session:', session ? 'EXISTS' : 'NULL');
        if (session) {
          console.log('[AuthContext] User ID:', session.user.id);
          console.log('[AuthContext] User email:', session.user.email);
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Resgatar convite pendente se existir e se acabamos de logar
        if (session && typeof window !== 'undefined') {
          const pendingToken = localStorage.getItem('pending_invite_token');
          if (pendingToken) {
            console.log('[AuthContext] Found pending invite token, redeeming...', pendingToken);
            supabase.rpc('redeem_invite', { invite_token: pendingToken })
              .then(({ error }) => {
                if (error) {
                  console.error('[AuthContext] Failed to redeem pending invite:', error);
                } else {
                  console.log('[AuthContext] Successfully redeemed pending invite!');
                  localStorage.removeItem('pending_invite_token');
                }
              });
          }
        }
        
        // 🔍 Sentry: Atualizar user context
        if (typeof window !== 'undefined') {
          import('@/lib/sentry').then(({ setUser: setSentryUser }) => {
            if (session?.user) {
              setSentryUser({
                id: session.user.id,
                email: session.user.email,
              });
            } else {
              setSentryUser(null);
            }
          });
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      console.log('[AuthContext] Initial session check...');
      console.log('[AuthContext] Session:', session ? 'EXISTS' : 'NULL');
      console.log('[AuthContext] Error:', error);
      
      if (session) {
        console.log('[AuthContext] User ID:', session.user.id);
        console.log('[AuthContext] User email:', session.user.email);
        console.log('[AuthContext] Session expires at:', new Date(session.expires_at! * 1000).toLocaleString());
      } else {
        console.warn('[AuthContext] No session found - user needs to login');
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      // Resgatar convite pendente se existir e se já estivermos logados
      if (session && typeof window !== 'undefined') {
        const pendingToken = localStorage.getItem('pending_invite_token');
        if (pendingToken) {
          console.log('[AuthContext] Found pending invite token (initial), redeeming...', pendingToken);
          supabase.rpc('redeem_invite', { invite_token: pendingToken })
            .then(({ error }) => {
              if (error) {
                console.error('[AuthContext] Failed to redeem pending invite:', error);
              } else {
                console.log('[AuthContext] Successfully redeemed pending invite!');
                localStorage.removeItem('pending_invite_token');
              }
            });
        }
      }

      // 🔍 Sentry: Atualizar user context inicial
      if (typeof window !== 'undefined') {
        import('@/lib/sentry').then(({ setUser: setSentryUser }) => {
          if (session?.user) {
            setSentryUser({
              id: session.user.id,
              email: session.user.email,
            });
          } else {
            setSentryUser(null);
          }
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, metadata?: Record<string, any>) => {
    // ✅ Validação client-side de senha forte
    if (password.length < 10) {
      return { 
        error: { 
          message: "Senha deve ter no mínimo 10 caracteres" 
        } 
      };
    }
    
    // Verificar força da senha via função do banco
    try {
      const { data: strength, error: strengthError } = await supabase
        .rpc('validate_password_strength', { password });
      
      if (strengthError) {
        console.error('[AuthContext] Erro ao validar força da senha:', strengthError);
        // Continuar mesmo se validação falhar (fallback)
      } else if (strength) {
        // @ts-ignore - validate_password_strength existe após aplicar migrations
        const result = Array.isArray(strength) ? strength[0] : strength;
        if (result && !result.is_strong) {
          return {
            error: {
              message: `Senha fraca: ${result.recommendations?.join(', ') || 'Use letras maiúsculas, minúsculas, números e caracteres especiais'}`
            }
          };
        }
      }
    } catch (err) {
      console.error('[AuthContext] Erro inesperado ao validar senha:', err);
      // Continuar mesmo se validação falhar (fallback)
    }
    
    // Use current origin for email confirmation redirect
    // Important: This URL must be configured in Supabase Auth settings
    const redirectUrl = `${window.location.origin}/auth/confirm`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          ...metadata
        }
      }
    });
    return { error };
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    // Se login bem-sucedido e "Lembrar-me" marcado, criar sessão
    if (!error && data.user && rememberMe) {
      await createRememberMeSession(data.user.id);
    }

    return { error, data };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    });
    return { error };
  };

  const signOut = async () => {
    // Remover sessão "Lembrar-me"
    await removeRememberMeSession();
    
    // Limpar cache de onboarding (segurança multitenant)
    const { clearOnboardingCache } = await import('@/lib/onboardingCache');
    clearOnboardingCache();
    
    await supabase.auth.signOut();
    // Redirecionar para página de login (sem redirect, pois é logout intencional)
    // Usar replace para não adicionar ao histórico
    window.location.replace('/entrar');
  };

  const logoutAllDevices = async () => {
    if (!user) {
      return { success: false, error: 'Usuário não autenticado' };
    }
    
    const result = await logoutAllDevicesLib(user.id);
    
    if (result.success) {
      await supabase.auth.signOut();
    }
    
    return result;
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
      logoutAllDevices
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}